import React, { useState } from 'react';
import { Instagram, Linkedin, Mail, Youtube, MapPin, Phone, Zap, ArrowRight, Sparkles } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  // Redes sociales con URLs fijas
  const socialLinks = {
    instagram: 'https://www.instagram.com/zenexpy/',
    linkedin: 'https://www.linkedin.com/company/zenex-paraguay',
    youtube: 'https://www.youtube.com/@Zenexpy',
    email: 'zenexparaguay@gmail.com'
  };

  // Navegación con scroll al top
  const handleNavigate = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [subscribing, setSubscribing] = useState(false);

  // Suscripción al newsletter
  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribing(true);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: 'Suscriptor Newsletter',
          email: email,
          message: 'Suscripcion al newsletter desde el footer',
          property_type: 'newsletter'
        })
      });
      if (res.ok) {
        setSubscribed(true);
        setEmail('');
        setTimeout(() => setSubscribed(false), 5000);
      }
    } catch (error) {
      console.error('Error al suscribir:', error);
    }
    setSubscribing(false);
  };

  return (
    <footer className="bg-zinc-900 text-white relative overflow-hidden" data-testid="footer">
      {/* Decorative background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#90EE90]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#90EE90]/3 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
      </div>

      {/* Newsletter Section - Innovador */}
      <div className="border-b border-zinc-800 relative z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
          <div className="bg-gradient-to-r from-[#14532D] to-[#1a5c34] rounded-2xl p-6 sm:p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6 sm:gap-8">
            <div className="flex items-start sm:items-center gap-3 sm:gap-4">
              <div className="bg-[#90EE90]/20 p-3 sm:p-4 rounded-full flex-shrink-0">
                <Sparkles className="text-[#90EE90] w-6 h-6 sm:w-8 sm:h-8" />
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1">Unite a la revolucion solar</h3>
                <p className="text-[#90EE90]/80 text-xs sm:text-sm md:text-base">Recibi novedades, promociones y tips de ahorro energetico</p>
              </div>
            </div>
            <form onSubmit={handleSubscribe} className="flex w-full md:w-auto gap-2">
              {subscribed ? (
                <div className="flex items-center gap-2 text-[#90EE90] font-semibold">
                  <Zap className="w-5 h-5" />
                  ¡Gracias por suscribirte!
                </div>
              ) : (
                <>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    className="flex-1 md:w-64 px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:border-[#90EE90] transition-colors"
                    required
                  />
                  <button
                    type="submit"
                    className="bg-[#90EE90] text-[#14532D] px-6 py-3 rounded-xl font-bold hover:bg-white transition-colors flex items-center gap-2 group"
                  >
                    Suscribirse
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
          
          {/* Brand - Logo + Slogan */}
          <div className="lg:col-span-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_6a2260fd-dccc-4f28-a590-11006a0f3879/artifacts/9g163i6b_9.png" 
              alt="Zenex Logo" 
              className="h-12 mb-6"
            />
            <p className="text-lg text-[#90EE90] font-semibold mb-4">
              Bienvenidos a la soberanía energética.
            </p>
            <p className="text-sm text-zinc-400 leading-relaxed mb-6 max-w-xs">
              Transformamos hogares y empresas en generadores de energía limpia. 
              Tu independencia energética empieza aquí.
            </p>
            
            {/* Social Icons */}
            <div className="flex gap-3">
              <a 
                href={socialLinks.instagram} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="bg-zinc-800 hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-500 p-3 rounded-xl transition-all duration-300 group"
                aria-label="Instagram"
              >
                <Instagram size={20} className="text-zinc-400 group-hover:text-white transition-colors" />
              </a>
              <a 
                href={socialLinks.linkedin} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="bg-zinc-800 hover:bg-[#0077B5] p-3 rounded-xl transition-all duration-300 group"
                aria-label="LinkedIn"
              >
                <Linkedin size={20} className="text-zinc-400 group-hover:text-white transition-colors" />
              </a>
              <a 
                href={socialLinks.youtube} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="bg-zinc-800 hover:bg-red-600 p-3 rounded-xl transition-all duration-300 group"
                aria-label="YouTube"
              >
                <Youtube size={20} className="text-zinc-400 group-hover:text-white transition-colors" />
              </a>
              <a 
                href={`mailto:${socialLinks.email}`}
                className="bg-zinc-800 hover:bg-[#90EE90] p-3 rounded-xl transition-all duration-300 group"
                aria-label="Email"
              >
                <Mail size={20} className="text-zinc-400 group-hover:text-[#14532D] transition-colors" />
              </a>
            </div>
          </div>

          {/* Servicios */}
          <div>
            <h4 className="text-lg font-semibold mb-5">
              Servicios
            </h4>
            <ul className="space-y-3 text-left">
              <li>
                <button
                  onClick={() => handleNavigate('/servicios/residenciales')}
                  className="text-sm text-zinc-400 hover:text-[#90EE90] transition-colors hover:translate-x-1 inline-block transform duration-200 text-left"
                >
                  Instalación Residencial
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleNavigate('/servicios/empresas')}
                  className="text-sm text-zinc-400 hover:text-[#90EE90] transition-colors hover:translate-x-1 inline-block transform duration-200 text-left"
                >
                  Instalación Comercial
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleNavigate('/servicios/rural-agro')}
                  className="text-sm text-zinc-400 hover:text-[#90EE90] transition-colors hover:translate-x-1 inline-block transform duration-200 text-left"
                >
                  Soluciones Rurales y Agro
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleNavigate('/productos/catalogo')}
                  className="text-sm text-zinc-400 hover:text-[#90EE90] transition-colors hover:translate-x-1 inline-block transform duration-200 text-left"
                >
                  Hardware Premium
                </button>
              </li>
            </ul>
          </div>

          {/* Empresa */}
          <div>
            <h4 className="text-lg font-semibold mb-5">Empresa</h4>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => handleNavigate('/proyectos')}
                  className="text-sm text-zinc-400 hover:text-[#90EE90] transition-colors hover:translate-x-1 inline-block transform duration-200"
                >
                  Proyectos
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleNavigate('/nosotros/sustentabilidad')}
                  className="text-sm text-zinc-400 hover:text-[#90EE90] transition-colors hover:translate-x-1 inline-block transform duration-200"
                >
                  Sustentabilidad
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleNavigate('/nosotros/historia')}
                  className="text-sm text-zinc-400 hover:text-[#90EE90] transition-colors hover:translate-x-1 inline-block transform duration-200"
                >
                  Nuestra Historia
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleNavigate('/noticias')}
                  className="text-sm text-zinc-400 hover:text-[#90EE90] transition-colors hover:translate-x-1 inline-block transform duration-200"
                >
                  Noticias
                </button>
              </li>
            </ul>
          </div>

          {/* Contacto */}
          <div>
            <h4 className="text-lg font-semibold mb-5">Contacto</h4>
            <ul className="space-y-4 text-sm text-zinc-400">
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-[#90EE90] flex-shrink-0" />
                <a href="https://wa.me/595991344956" target="_blank" rel="noopener noreferrer" className="hover:text-[#90EE90] transition-colors">
                  +595 991 344956
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#90EE90] flex-shrink-0" />
                <a href="mailto:zenexparaguay@gmail.com" className="hover:text-[#90EE90] transition-colors">
                  zenexparaguay@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#90EE90] mt-1 flex-shrink-0" />
                <a 
                  href="https://maps.app.goo.gl/zCbm8TZaPdLMUxEG6" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-[#90EE90] transition-colors"
                >
                  Facundo Machain 7390,<br />
                  casi Francis Morices<br />
                  Asunción, Paraguay
                </a>
              </li>
            </ul>
            <button
              onClick={() => handleNavigate('/calculadora')}
              className="mt-6 bg-[#90EE90] text-[#14532D] hover:bg-white px-6 py-3 rounded-xl font-bold text-sm transition-all duration-300 flex items-center gap-2 group"
            >
              Calculá tu Ahorro
              <Zap className="w-4 h-4 group-hover:animate-pulse" />
            </button>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-zinc-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-zinc-500">
              © {currentYear} Zenex Paraguay. Todos los derechos reservados.
            </p>
            <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-sm text-zinc-500">
              <Link 
                to="/politica-privacidad" 
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="hover:text-[#90EE90] transition-colors"
              >
                Pol. de Privacidad
              </Link>
              <Link 
                to="/terminos-servicio"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="hover:text-[#90EE90] transition-colors"
              >
                Térm. de Servicio
              </Link>
              <a 
                href="/admin" 
                className="text-zinc-600 hover:text-zinc-400 transition-colors text-xs"
                data-testid="admin-access-link"
              >
                Admin
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
